import { useMemo, type PropsWithChildren } from "react";

import { createContext } from "@radix-ui/react-context";
import { Navigate } from "@tanstack/react-router";

import type { EnhancedMultisensor } from "~/types/multisensor.type";
import type { EnhancedRoom } from "~/types/room.type";
import type { EnhancedValve } from "~/types/valve.type";

import { useBuildingContext } from "./building.provider";
import { useFloorContext } from "./floor.provider";

interface RoomProviderProps extends PropsWithChildren {
  roomId: string;
}

interface RoomContextValue {
  room: EnhancedRoom;
  valves: EnhancedValve[];
  multisensors: EnhancedMultisensor[];
}

const [RoomContextProvider, useRoomContext] = createContext<RoomContextValue>("RoomProvider");

const RoomProvider: React.FC<RoomProviderProps> = ({ roomId, children }) => {
  const { building, rooms } = useBuildingContext("RoomProvider");
  const { floor, valves, multisensors } = useFloorContext("RoomProvider");

  const room = useMemo(() => rooms.find(r => r.id === roomId), [rooms, roomId]);
  if (!room) {
    throw new Error(`Room with id ${roomId} not found`);
  }

  const roomValves = useMemo(() => valves.filter(v => v.roomId === roomId), [valves, roomId]);
  const roomMultisensors = useMemo(() => multisensors.filter(m => m.room.id === roomId), [multisensors, roomId]);

  const shouldRedirect = room.floor.id !== floor.id;
  if (shouldRedirect)
    return (
      <Navigate
        to={"/buildings/$buildingId/floors/$floorId/rooms/$roomId"}
        params={{ buildingId: building.id, floorId: room.floor.id, roomId }}
      />
    );

  return (
    <RoomContextProvider room={room} valves={roomValves} multisensors={roomMultisensors}>
      {children}
    </RoomContextProvider>
  );
};

export { RoomProvider, useRoomContext };
